import { FunctionComponent, useMemo, useState } from 'react';
import GenericModal from '../../../ui/GenericModal';
import SearchBar from '../../../ui/inputs/SearchBar';
import { Column, Table } from '@tanstack/react-table';
import { grey } from '@mui/material/colors';
import EditIcon from '@mui/icons-material/Edit';
import RaptorToolTip from '../../../ui/RaptorTooltip';
import { UltraTablePreset } from '../types/presets.types';

interface PopoverGroupsType {
  name: string;
  columns: string[];
}

const PopoverGroups: PopoverGroupsType[] = [
  //Changing standard to meet mancopositions columns

  {
    name: 'Standard',
    columns: [
      'name',
      'gm_name',
      'gm_name_long',
      'fund_id',
      'fund_name',
      'isin',
      'instrument_title',
      'instrument',
      'instrument_id',
      'instrument_type_id',
      'instrument_description_title',
      'asset_class',
      'asset_type',
      'currency',
      'client_price',
      'fx',
      'lc_exposure',
      'lc_exposure_pct',
      'bc_exposure',
      'bc_exposure_pct',
      'gross_exposure',
      'gross_exposure_pct',
      'commitment',
      'commitment_pct',
      'mr_position',
      'used_underlying_price',
      'cash_type',
      'volatility',
      'base_currency',
      'gamma',
      'vega',
      'asset_subclass_name',
    ],
  },
  {
    name: 'Indicative',
    columns: [
      'yield',
      'coupon',
      'coupon_payment_period',
      'gm_client_name',
      'duration',
      'country_code',
      'credit_rating',
      'credit_type',
      'days_to_maturity',
      'time_to_maturity',
      'maturity_time',
      'maturity_date',
      'interest_rate',
      'risk_factor',
      'outstanding_securities',
      'market_value_local_currency',
      'expiry_date',
      'rating',
      'term',
      'outstanding_capital',
      'issue_date',
      'fund_assets_currency',
      'market_value_base_currency',
      'strike',
      'oas',
      'share_class',
    ],
  },
  {
    name: 'Sector Data',
    columns: [
      'icb_sector_id',
      'icb_sector_name',
      'msci_sector_code',
      'msci_sector_id',
      'msci_sector_name',
      'sector_id',
      'sector_name',
      'sector_icb_name',
      'msci_sector_code',
      'sector_icb_id',
    ],
  },
  {
    name: 'Instrument',
    columns: [
      'cis',
      'is_cb',
      'is_cds',
      'is_etf',
      'is_sfi',
      'is_coco',
      'is_at1',
      'asset_form',
      'client_etf_type',
      'counterparty_names',
      'six_borrower_category_description',
      'is_loan',
      'is_subordinated',
      'is_hybrid',
      'is_trs',
      'is_govt_owned',
      'is_certificate',
      'is_spac',
      'is_index_linked',
      'interest_type',
    ],
  },
  {
    name: 'Valuation',
    columns: [
      'price',
      'unrealised_pl',
      'unrealised_pl_base',
      'unrealised_pl_local',
      'market_price',
    ],
  },
  {
    name: 'Leverage',
    columns: ['da_son_exposure', 'notional_amount', 'sum_of_notionals'],
  },
  {
    name: 'AML',
    columns: ['aml_exposure', 'country_code_risk_level', 'is_listed'],
  },
  {
    name: 'Sensitivity',
    columns: ['cs01', 'dv01', 'delta'],
  },
];

// ADEPA SPECIFIC COLUMNS?
// asset_class_code
// adepa_derivative_hedge
//asset_subclass_code

// GEMINI SPECIFIC COLUMNS?
// client_country_code

// Random client speicfic
// is_govt_owned

const EXCLUDED_COLUMNS: string[] = [
  'gm_id',
  'gm_id_new',
  'g_key',
  'position_id',
  'source',
  'client_price_date',
  'pricing_assetsubclass',
  'counterparty_type_id',
  'valid_isin',
  'expiry_check_skip',
  'portfolio_id',
  'outstanding_capital_six',
  'has_voting_rights',
  'sheet_type',
  'category_id',
  'maturity_check_skip',
  'outstanding_values_date',
  'gm_client_name',
  'g_name',
  'outstanding_securities_six',
  'proxy_isin',
  'mic_code',
  'counterparty_id',
  'six_borrower_category_id',
  'day_count',
  'raw_position',
  'six_borrower_category_description',
  'forward_rate',
  'n_contract',
  'reportability',
  'swap_curve_risk_factors',
  'hedge_trade',
  'risk_metric_value',
  'pricing_assetclass',
  'fund_assets',
  'asset_class_code',
  'hedged_class',
  'annuity_price',
  'client_country_code',
  'asset_subclass_code',
];

// Adding setSelectedPreset might hit performance because it forces re-render to whole table for UI button changes

interface ColumnPopupSelectorProps<T> {
  table: Table<T>;
  handleResetFilters: () => void;
  setSelectedPreset: React.Dispatch<
    React.SetStateAction<UltraTablePreset | undefined>
  >;
}

const ColumnPopupSelector: FunctionComponent<ColumnPopupSelectorProps<any>> = ({
  table,
  handleResetFilters,
  setSelectedPreset,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  // Memo just incase the columns are dynamic triggered from outside the component / ie filters
  const groupedColumns = useMemo(() => {
    const groupMap: Record<string, Column<any, unknown>[]> = {};

    PopoverGroups.forEach((group) => {
      groupMap[group.name] = [];
    });
    groupMap['Misc'] = [];

    const filteredColumns = table
      .getAllColumns()
      .filter((col) => !EXCLUDED_COLUMNS.includes(col.id));

    filteredColumns.forEach((col) => {
      if (col.id === 'index') return;

      const foundGroup = PopoverGroups.find((group) =>
        group.columns.includes(col.id),
      );

      if (col.columnDef.meta?.editable) {
        groupMap['Editable'] = groupMap['Editable'] || [];
        groupMap['Editable'].push(col);
      } else if (foundGroup) {
        groupMap[foundGroup.name].push(col);
      } else {
        groupMap['Misc'].push(col);
      }
    });

    const orderedGroups: Record<string, Column<any, unknown>[]> = Object.keys(
      groupMap,
    ).reduce((obj, key) => {
      if (key === 'Editable') {
        return { [key]: groupMap[key], ...obj };
      }
      return { ...obj, [key]: groupMap[key] };
    }, {});

    return orderedGroups;
  }, [table]);

  const choosableColumns = useMemo(
    () => Object.values(groupedColumns).flat(),
    [groupedColumns],
  );
  const allChoosableSelected = choosableColumns.every((col) =>
    col.getIsVisible(),
  );

  function toggleAllVisible() {
    const nextVisible = !allChoosableSelected;
    choosableColumns.forEach((column) => {
      column.toggleVisibility(nextVisible);
    });
    setSelectedPreset(undefined);
  }

  return (
    <>
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className="rounded-md bg-white px-4 py-2 text-[12px] font-medium tracking-tight text-gray-700 transition hover:bg-neutral-100"
      >
        CHOOSE COLUMNS
      </button>
      <GenericModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        className="max-w-fit"
        staticSize
      >
        <div className="flex border-spacing-1 items-center justify-between border-b border-gray-200 px-4 py-2">
          <SearchBar searchHandler={setSearchValue} />
          <div className="flex gap-1">
            <button
              className="rounded-md bg-white px-4 py-2 font-medium tracking-tight text-gray-700 shadow-sm transition hover:bg-neutral-100"
              onClick={toggleAllVisible}
            >
              {allChoosableSelected ? 'Deselect All' : 'Select All'}
            </button>
            <button
              className="rounded-md bg-white px-4 py-2 font-medium tracking-tight text-gray-700 shadow-sm transition hover:bg-neutral-100"
              onClick={handleResetFilters}
            >
              Reset Selection
            </button>
          </div>
        </div>

        <div className="space-y-4 p-2">
          {Object.entries(groupedColumns).map(([groupName, columns]) => {
            const filteredCols = columns.filter((col) => {
              const colName =
                col?.columnDef.meta?.short_title?.toString() ||
                col?.columnDef.header?.toString();
              return colName?.toLowerCase().includes(searchValue.toLowerCase());
            });

            if (filteredCols.length === 0) return null;

            return (
              <div key={groupName}>
                <h3 className="mb-1 text-sm font-bold uppercase tracking-wide text-mainblue">
                  {groupName}
                </h3>
                <div className="grid gap-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
                  {filteredCols.map((col) => {
                    const isEditable = col.columnDef.meta?.editable;

                    return (
                      <div key={col.id} className="group relative">
                        <button
                          onClick={() => {
                            setSelectedPreset(undefined);
                            col.toggleVisibility();
                          }}
                          className={`group flex w-full items-center gap-1 rounded-lg border border-gray-100 px-3 py-1.5 hover:border-blue-300 hover:bg-blue-50 focus:border-blue-300 ${col.getIsVisible() ? 'bg-blue-50' : ''}`}
                        >
                          {isEditable && (
                            <EditIcon
                              style={{ fontSize: '1.12rem', color: grey[500] }}
                            />
                          )}
                          <p
                            className={`${isEditable ? 'font-bold text-green-600' : ''} whitespace-nowrap text-sm/6`}
                          >
                            {col?.columnDef.meta?.short_title?.toString() ||
                              col?.columnDef.header?.toString()}
                          </p>
                        </button>
                        {isEditable && <RaptorToolTip text="Editable" />}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </GenericModal>
    </>
  );
};

export default ColumnPopupSelector;
